import {InputNumber, Modal, Switch} from 'antd'
import {
    course_getAvailSubjectsForCourse_Api,
    course_linkSubjectToCourse_Api,
    course_unlinkSubjectToCourse_Api,
} from 'apis'
import {getId} from 'apis/model/base'
import {showConfirm} from 'components/Feed/TimeLineFeed'
import classNames from 'classnames'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo, useState} from 'react'
import {FaFilter} from 'react-icons/fa6'
import {BsGrid, BsInboxesFill, BsUiRadiosGrid} from 'react-icons/bs'
import {NullAsDefault, pagerControls, renderSelf} from 'views/Shared'
import {renderCourseSubjectCard} from '../../../Workspace/Course/functions/renderCourseSubject'
import EntityList from '../../EntityList'
import {notifyOnError} from '../../factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from '../../factory/createNotification'
import {bindQueryParam} from '../../functions/routerHelper'
import {parseNumber} from "helpers/parseNumber";
import {formatterNumber} from "helpers/formatterNumber";
import {Formik, useFormikContext} from "formik";
import {renderIf, shallowDiff} from "../../../Shared";
import {FormActionGroups} from "../../../../components/form/FormActionBar";
import {course_editSubjectParamInCourse_Api} from "../../../../apis";
import {PiFadersFill} from "react-icons/pi";
import LookupField from "../../../../components/LookupField";
import {getType} from "../../../../apis/model/base";
import TooltipApp from "components/TooltipApp";
import getTitle from "../../../../helpers/getTitle";
import {ActionModalWrapper} from "../../../../components/form/ActionModalWrapper";
import LoadingBlock from "../../../Loading/LoadingBlock";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {Link} from "react-router-dom";
import {Field, Section} from "../../../../components/form/ModernForm";
import {GoDotFill} from "react-icons/go";

const subject_actions = {
    link: {label: 'selected', value: 1, action: 'active'},
    unLink: {label: 'no', value: 0, action: 'inactive'},
}

function confirmAction(params) {
    const {title, content, Icon = NullAsDefault} = params ?? {}
    showConfirm({
        icon: Icon,
        okButtonProps: {
            type: 'primary',
            className: 'rounded-lg no-shadow no-text-shadow no-border',
        },
        cancelButtonProps: {
            type: 'default',
            className: 'rounded-lg no-shadow no-text-shadow no-border',
        },
        title: {title},
        content: {content},
        ...(params ?? {}),
    })
}


// ─── ModernForm field helpers (đọc/ghi thẳng qua Formik context) ─────────────

// Ô nhập số - giữ nguyên hành vi createNumberControlledInput (min 0, parser/formatter chung), chỉ
// bỏ lớp FieldsFactory. value undefined -> để trống (không ép về 0) để shallowDiff không sinh thay
// đổi giả cho các field ưu tiên không nhập.
const NumberField = ({name, disabled, addonBefore, placeholder}) => {
    const {values, setFieldValue} = useFormikContext()
    const raw = values?.[name]
    return (
        <InputNumber
            disabled={disabled}
            min={0}
            parser={parseNumber}
            formatter={formatterNumber}
            addonBefore={addonBefore}
            placeholder={placeholder}
            className="w-full rounded"
            value={_.isNil(raw) ? null : Number(raw)}
            onChange={(value) => setFieldValue(name, value)}
        />
    )
}

const MandatorySwitch = () => {
    const t = useTranslate()
    const {values, setFieldValue} = useFormikContext()
    return (
        <Switch
            style={{width: 'fit-content'}}
            checked={!!values?.is_mandatory}
            checkedChildren={t('yes')}
            onChange={(checked) => setFieldValue('is_mandatory', Number(checked ?? 0))}
        />
    )
}

// Tên nhóm phân loại - port nguyên từ formSchema.group_name (LookupField theo owner của môn).
const GroupNameField = () => {
    const t = useTranslate()
    const {values, setFieldValue} = useFormikContext()
    const owner = _.get(values, 'subject.owner', {})
    return (
        <LookupField
            createIfNotExists={true}
            name="group_name"
            defaultValue={values?.group_name}
            placeholder={t('choose or create')}
            apiPath={`/lookup/${getType(owner)}/${getId(owner)}/field/course_subject_group_name/gets`}
            handleChange={(e) => setFieldValue('group_name', e.target.value)}
        />
    )
}

// 1 bộ "điều kiện đạt" = điểm TB tối thiểu + % chuyên cần tối thiểu. Dùng 3 lần: bộ chính (bắt
// buộc) + 2 bộ ưu tiên thay thế (để trống/0 = không xét).
const PassingConditionRow = ({
                                 label, scoreName, attendanceName,
                                 required = false, scoreHint, attendanceHint,
                                 errors = {}, touched = {},
                             }) => {
    const t = useTranslate()
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-color-200 tracking-wider">
                {label}
                {required === true && <span style={{fontSize: 14}} className="text-rose-600 ml-0.5">*</span>}
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field
                    required={required}
                    hint={scoreHint}
                    error={touched[scoreName] && errors[scoreName]}>
                    <NumberField
                        name={scoreName}
                        placeholder={t('passed score')}
                        addonBefore={<span className="font-medium text-color-300 italic">{t('score')}</span>}
                    />
                </Field>
                <Field
                    required={required}
                    hint={attendanceHint}
                    error={touched[attendanceName] && errors[attendanceName]}>
                    <NumberField
                        name={attendanceName}
                        placeholder={t('passed attendance percentage')}
                        addonBefore={<span className="font-medium text-color-300 italic">{`${t('cc_percent')} (%)`}</span>}
                    />
                </Field>
            </div>
        </div>
    )
}


export const SubjectInCourseEditForm = ({
                                            onCancel = NullAsDefault,
                                            onSubmit = NullAsDefault,
                                            initialValues = {},
                                        }) => {
    const t = useTranslate()

    const _initialValues = useMemo(() => {
        const {...rest} = initialValues ?? {}
        return {
            is_mandatory: 0,
            ...rest
        }
    }, [initialValues])

    const validate = (values) => {
        let errors = {}

        const {number_of_sessions, number_of_credits, passed_score, passed_attendance_percentage} = values ?? {}

        if (!number_of_sessions) errors.number_of_sessions = t('required field')
        if (!number_of_credits) errors.number_of_credits = t('required field')
        if (!passed_score) errors.passed_score = t('required field')
        if (!passed_attendance_percentage) errors.passed_attendance_percentage = t('required field')

        return errors
    }

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                const {...rest} = changes ?? {}
                onSubmit(_.omitBy({...rest}, _.isUndefined))
            }}>
            {({errors = {}, touched = {}}) => (
                <React.Fragment>
                    <div className="mb-4 p-3 flex flex-col gap-1 rounded-xl bg-background-50">
                        <div className="font-bold text-base text-gray-700 tracking-wide">
                            {getTitle(initialValues?.subject)}
                        </div>
                        <div className="text-xs text-color-300 italic tracking-wider leading-relaxed">
                            {`* ${t('update course subject description')}`}
                        </div>
                    </div>

                    <ActionModalWrapper
                        className="gap-4"
                        action={<FormActionGroups submitProps={{label: 'save'}}/>}>

                        <Section icon={PiFadersFill} title={t('classifying content')}>
                            <Field label={t('group name')}>
                                <GroupNameField/>
                            </Field>
                        </Section>

                        <Section icon={BsUiRadiosGrid} title={t('the classification attributes of academic points')}>
                            <Field label={t('is mandatory subject')} hint={t('is mandatory subject description')}>
                                <MandatorySwitch/>
                            </Field>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field
                                    required
                                    label={t('number of sessions')}
                                    error={touched.number_of_sessions && errors.number_of_sessions}>
                                    <NumberField disabled={true} name="number_of_sessions" placeholder={t('ex : 30')}/>
                                </Field>
                                <Field
                                    required
                                    label={t('number of credits')}
                                    error={touched.number_of_credits && errors.number_of_credits}>
                                    <NumberField name="number_of_credits" placeholder={t('ex : 30')}/>
                                </Field>
                            </div>
                        </Section>

                        <Section
                            icon={BsGrid}
                            title={t('subject completion requirements')}
                            subtitle={t('passing requirement description')}>
                            <PassingConditionRow
                                required
                                label={t('passing requirement')}
                                scoreName="passed_score"
                                attendanceName="passed_attendance_percentage"
                                scoreHint={t('passed score field hint')}
                                attendanceHint={t('passed attendance percentage field hint')}
                                errors={errors}
                                touched={touched}
                            />
                            <PassingConditionRow
                                label={t('passing requirement (2nd)')}
                                scoreName="passed_score_1"
                                attendanceName="passed_attendance_percentage_1"
                            />
                            <PassingConditionRow
                                label={t('passing requirement (3rd)')}
                                scoreName="passed_score_2"
                                attendanceName="passed_attendance_percentage_2"
                            />
                            <span className="text-2xs text-color-400 italic leading-snug">
                                {t('passing requirement alt description')}
                            </span>
                        </Section>

                    </ActionModalWrapper>
                </React.Fragment>
            )}
        </Formik>
    )
}

export const CourseSubjectList = ({
                                      item,
                                      header = null,
                                      controls = pagerControls,
                                      renderFooter = renderSelf,
                                  }) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const [refreshToken, setRefreshToken] = useState()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    // Bộ lọc nâng cao - 2 tiêu chí đúng 2 thứ đang hiện sẵn trên mỗi thẻ môn học (chip "Môn học bắt
    // buộc" + Switch chọn/bỏ chọn ở renderCourseSubjectCard): is_mandatory (loại môn) và is_linked
    // (đã áp dụng cho khóa này chưa). Giá trị cố tình để STRING '1'/'0' (không phải number) - callApi
    // (modules/asyncCache/callApi.js) build query GET qua `if (values[key])`, number 0 bị coi là
    // falsy nên sẽ RỚT mất khỏi query string; string '0' vẫn truthy nên đi qua được, BE tự ép kiểu
    // lại (int) khi đọc.
    const [is_mandatory, setIsMandatory] = useState()
    const [is_linked, setIsLinked] = useState()
    const [showMore, setShowMore] = useState(false)
    const hasAdvancedFilters = !_.isNil(is_mandatory) || !_.isNil(is_linked)

    const onCancel = () => {
        setAction({})
    }


    const apiInfo = course_getAvailSubjectsForCourse_Api

    const {keyword, component: search_widget} = controls?.search ?? {}

    const course_id = getId(item)

    const refreshKey = useMemo(
        () =>
            [
                apiInfo.path.replace(':id', course_id),
                JSON.stringify(keyword),
                is_mandatory,
                is_linked,
                refreshToken,
            ].join(','),
        [course_id, keyword, is_mandatory, is_linked, refreshToken]
    )

    const onError = notifyOnError(t)

    const refresh = () => {
        setRefreshToken(Date.now())
    }

    const {handleAsyncAction: addSubject} = useAsyncAction({
        apiInfo: course_linkSubjectToCourse_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            refresh()
        },
    })

    const {handleAsyncAction: removeSubject} = useAsyncAction({
        apiInfo: course_unlinkSubjectToCourse_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            refresh()
        },
    })

    const {handleAsyncAction: editSubjectInfo} = useAsyncAction({
        apiInfo: course_editSubjectParamInCourse_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            refresh()
            onCancel()
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case "EDIT_SUBJECT_INFO_IN_COURSE":
                setAction({type: action, value: params})
                break
            case subject_actions.link.action:
                const {course, subject} = params ?? {}
                confirmAction({
                    Icon: <BsUiRadiosGrid style={{color: '#48bb78'}} className="anticon"/>,
                    okText: t('choose'),
                    cancelText: t('ignore'),
                    title: (
                        <span className="font-bold text-color-000">{t('choose subject')}</span>
                    ),
                    content: (
                        <span className="text-color-500 italic">
              {t('are you sure you want to choose this course class')}
            </span>
                    ),
                    onOk: () => {
                        addSubject(
                            {},
                            {course_id: getId(course), subject_id: getId(subject)}
                        )
                    },
                })
                break
            case subject_actions.unLink.action:
                const course_subject_id = params?.id
                if (course_subject_id) {
                    confirmAction({
                        Icon: <BsGrid style={{color: 'var(--color-primary-300)'}} className="anticon"/>,
                        okText: t('sure'),
                        cancelText: t('ignore'),
                        title: (
                            <span className="font-bold text-color-000">{t('deselect subjects')}</span>
                        ),
                        content: (
                            <span className="text-color-500 italic">
                {t('are you sure you want to deselect subjects of class')}
              </span>
                        ),
                        onOk: () => {
                            removeSubject({}, {id: course_subject_id})
                        },
                    })
                }
                break
            default:
                break
        }
    }

    const renderActions = (item) => {
        return (
            <div className="flex flex-center gap-x-3">
                {renderIf(
                    item?.is_linked,
                    <TooltipApp
                        title={t('update subject info for course')}
                        placement="left"
                        style={{body: {
                                minHeight: 30,
                                fontSize: 12,
                                fontStyle: 'italic',
                            }}}>
                        <div
                            onClick={() => {
                                if (handleAction) {
                                    handleAction("EDIT_SUBJECT_INFO_IN_COURSE", item);
                                }
                            }}
                            style={{ width: 26, height: 26, borderRadius: 999 }}
                            className="flex flex-center bg-secondary-100 hover:bg-secondary-600 text-secondary-600 hover:text-white cursor-pointer">
                            <PiFadersFill size={16}/>
                        </div>
                    </TooltipApp>
                )}
                <Switch
                    size={isLg ? 'default' : 'small'}
                    // className="custom-switch-xs"
                    checked={Number(!!item?.is_linked)}
                    onChange={(value) => {
                        const current = _.find(subject_actions, {
                            value: Number(value),
                        })
                        handleAction(current?.action, item)
                    }}
                />
            </div>
        )
    }

    const modal = useMemo(() => {
        let title
        let Content = NullAsDefault
        let params = {title: undefined}
        let modal_params = {}
        let Wrapper = ({children}) => children({item: {}});
        switch (action?.type) {
            case "EDIT_SUBJECT_INFO_IN_COURSE":
                title = (
                    <div className="uppercase text-center font-bold text-color-000 tracking-wide">
                        {t('update subject info for course')}
                    </div>
                )
                params = {
                    initialValues: action?.value,
                    onCancel,
                    onSubmit: (values) => {
                        editSubjectInfo(
                            values,
                            {
                                id: action?.value?.id,
                            }
                        )
                    }
                }
                modal_params = {
                    width: 600,
                }
                Content = SubjectInCourseEditForm
                break
            default:
                break
        }
        return (
            <Modal
                footer={null}
                title={title}
                onCancel={onCancel}
                destroyOnHidden={true}
                open={action?.type}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Content
                                {...params}
                                {...{item}}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [action])

    return (
        <ActionModalWrapper className="flex-1">
            {header}
            <div className="mb-4 p-3 border border-blue-300 bg-blue-50 rounded-xl text-xs flex flex-col gap-0.5">
                <span className="font-semibold text-blue-700 italic">
                    {`* ${t('note')}: `}
                </span>
                <span className="ml-2.5 text-blue-500 tracking-wider">
                    {t('course subject modal description')}
                </span>
                <div className="mt-2 ml-2.5 text-gray-700 tracking-wider">
                    <GoDotFill size={10} className="mr-1"/>
                    {t(
                        'go to subject add new',
                        {action: (
                            <Link to="/workspace/course?rel=subject" className="font-bold italic no-underline">
                                {t('subject library')}
                            </Link>
                        )}
                    )}
                </div>
            </div>
            <div className="flex flex-col flex-1">
                <h3 className="font-bold text-base text-slate-600 tracking-wide">
                    {t('subjects')} ({_.get(item, 'subjects', []).length})
                </h3>
                <div style={{height: 3}} className="my-1 w-20 rounded-full bg-gray-400"/>
            </div>
            <div className="flex flex-col gap-4 flex-1">
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <div className="flex-1">{search_widget}</div>
                        <button
                            type="button"
                            onClick={() => setShowMore((v) => !v)}
                            style={{width: 40, height: 40, borderRadius: '0.5rem', cursor: 'pointer', position: 'relative'}}
                            className={classNames(
                                "flex flex-center transition-all border duration-300",
                                (showMore || hasAdvancedFilters) ?
                                    'bg-blue-600 border-blue-600 text-white' :
                                    'hover:scale-96 background hover:bg-blue-100 border-blue-300 hover:border-blue-500 text-blue-400 hover:text-blue-500'
                            )}>
                            <FaFilter size={16} className="transition-transform duration-200"/>
                            {hasAdvancedFilters && (
                                <span style={{top: -3, right: -3}} className="w-3 h-3 rounded-full bg-primary border-2 border-white absolute"/>
                            )}
                        </button>
                    </div>
                    {/* Panel expand in-flow (không phải dropdown/popover nổi) - đúng convention
                        filter chung của hệ thống (SongsPage/BibleListView/CourseClassStudentTab...),
                        class .mass-time-filter-advance tự vẽ mũi tên nối lên nút phễu. */}
                    {showMore && (
                        <div style={{'--filter-advance-border': '#3182ce'}} className="p-3 flex flex-col gap-4 shadow-md mass-time-filter-advance">
                            <div className="flex flex-col gap-1.5">
                                <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                                    {t('course subject filter type title')}
                                </span>
                                <div className="flex flex-wrap gap-1.5">
                                    {[
                                        {value: undefined, label: t('all')},
                                        {value: '1', label: t('course subject filter type mandatory')},
                                        {value: '0', label: t('course subject filter type optional')},
                                    ].map(({value, label}) => (
                                        <span
                                            key={label}
                                            className={classNames(
                                                "px-3 py-1 text-xs font-medium rounded-lg border transition-colors cursor-pointer select-none",
                                                is_mandatory === value
                                                    ? "bg-indigo-600 border-indigo-600 text-white"
                                                    : "background border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500"
                                            )}
                                            onClick={() => setIsMandatory(value)}>
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                                    {t('course subject filter status title')}
                                </span>
                                <div className="flex flex-wrap gap-1.5">
                                    {[
                                        {value: undefined, label: t('all')},
                                        {value: '1', label: t('selected')},
                                        {value: '0', label: t('course subject filter status unselected')},
                                    ].map(({value, label}) => (
                                        <span
                                            key={label}
                                            className={classNames(
                                                "px-3 py-1 text-xs font-medium rounded-lg border transition-colors cursor-pointer select-none",
                                                is_linked === value
                                                    ? "bg-indigo-600 border-indigo-600 text-white"
                                                    : "background border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500"
                                            )}
                                            onClick={() => setIsLinked(value)}>
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-col">
                    <EntityList
                        key={refreshKey}
                        apiInfo={apiInfo}
                        values={{keyword, is_mandatory, is_linked}}
                        refreshKey={refreshKey}
                        RenderEntity={React.Fragment}
                        PaginationMeta={NullAsDefault}
                        query={bindQueryParam({id: course_id})}
                        renderItem={renderCourseSubjectCard({renderActions, handleAction})}
                        renderWrap={(children) => (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {children}
                            </div>
                        )}
                        renderEmpty={() => (
                            <div className="p-6 flex flex-col items-center gap-3 border border-color-50 rounded-xl shadow-md">
                                <BsInboxesFill size={40} style={{color: '#dedede'}}/>
                                <span className="text-center text-sm text-color-300 tracking-wider italic">
                                    {t('no data is available now')}
                                </span>
                            </div>
                        )}
                    />
                </div>
            </div>
            {renderFooter(null)}
            {modal}
        </ActionModalWrapper>
    )
}

